import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Form, Alert, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { Check as FaCheck, X as FaTimes, Clock as FaUserClock, ClipboardList as FaClipboardList } from 'lucide-react';
import api from '../../api/axios';
import Loader from '../../components/Loader';

const VACATION_TYPES = {
  wypoczynkowy: { color: '#0d6efd', bg: '#e7f1ff', label: 'Wypoczynkowy' },
  okolicznościowy: { color: '#f59f00', bg: '#fff9db', label: 'Okolicznościowy' },
  bezpłatny: { color: '#e03131', bg: '#fff5f5', label: 'Bezpłatny' },
};

function formatDate(s) {
  return new Date(s).toLocaleDateString('pl-PL');
}

function EmployerWorkerVacationRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [rejectingId, setRejectingId] = useState(null);
  const [responseNote, setResponseNote] = useState('');

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/employer/vacation-requests/pending/');
      setRequests(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (id) => {
    setError('');
    try {
      await api.post(`/employer/vacation-requests/${id}/respond/`, { action: 'approve' });
      setSuccess('Wniosek został zaakceptowany');
      setRequests(prev => prev.filter(r => r.id !== id));
      window.dispatchEvent(new Event('vacationRequestsUpdated')); // <-- dodaj
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się zaakceptować wniosku');
    }
  };

  const handleReject = async (id) => {
    setError('');
    try {
      await api.post(`/employer/vacation-requests/${id}/respond/`, {
        action: 'reject',
        response_note: responseNote,
      });
      setSuccess('Wniosek został odrzucony');
      setRequests(prev => prev.filter(r => r.id !== id));
      setRejectingId(null);
      setResponseNote('');
      window.dispatchEvent(new Event('vacationRequestsUpdated')); // <-- dodaj
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się odrzucić wniosku');
    }
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container fluid className="px-2 px-md-4">

        <motion.div {...fadeInUp} className="mb-4">
          <h1 className="pub-h2 mb-1">Wnioski urlopowe pracowników</h1>
          <p className="pub-muted mb-0">Zaakceptuj lub odrzuć wnioski od połączonych pracowników</p>
        </motion.div>

        {success && <Alert variant="success" className="border-0" style={{ borderRadius: '15px' }}>{success}</Alert>}
        {error && <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>{error}</Alert>}

        <motion.div {...fadeInUp}>
          <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
            <Card.Header
              className="d-flex align-items-center gap-2"
              style={{ padding: '1.25rem' }}
            >
              <FaClipboardList color="var(--pub-navy)" />
              <h6 className="mb-0 fw-bold">Oczekujące wnioski ({requests.length})</h6>
            </Card.Header>
            <Card.Body className="p-3">
              {loading ? (
                <Loader />
              ) : requests.length === 0 ? (
                <div className="text-center py-5">
                  <FaUserClock size={40} className="text-muted mb-3" />
                  <p className="text-muted mb-0">Brak oczekujących wniosków urlopowych.</p>
                </div>
              ) : (
                <AnimatePresence>
                  {requests.map(r => {
                    const typeMeta = VACATION_TYPES[r.vacation_type];
                    return (
                      <motion.div
                        key={r.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        className="p-3 mb-2"
                        style={{
                          borderRadius: '14px',
                          background: typeMeta?.bg || '#f8f9fa',
                          border: `1px solid ${typeMeta?.color}33`,
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span style={{ color: '#212529', fontWeight: 700, fontSize: '15px' }}>
                                {r.worker_name}
                              </span>
                              <Badge style={{ background: typeMeta?.color, fontSize: '11px' }}>
                                {typeMeta?.label}
                              </Badge>
                              <Badge bg="secondary" style={{ fontSize: '11px' }}>
                                {r.days_count} dni
                              </Badge>
                            </div>
                            <div style={{ color: '#495057', fontSize: '13px', marginTop: '4px' }}>
                              {formatDate(r.start_date)}
                              {r.start_date !== r.end_date && ` – ${formatDate(r.end_date)}`}
                            </div>
                            {r.notes && (
                              <div className="text-muted small mt-1">{r.notes}</div>
                            )}
                          </div>

                          {rejectingId !== r.id && (
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-success d-flex align-items-center gap-1"
                                style={{ borderRadius: '10px' }}
                                onClick={() => handleApprove(r.id)}
                              >
                                <FaCheck size={11} /> Akceptuj
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                style={{ borderRadius: '10px' }}
                                onClick={() => { setRejectingId(r.id); setResponseNote(''); }}
                              >
                                <FaTimes size={11} /> Odrzuć
                              </button>
                            </div>
                          )}
                        </div>

                        {rejectingId === r.id && (
                          <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${typeMeta?.color}33` }}>
                            <Form.Group className="mb-2">
                              <Form.Label className="small fw-medium" style={{ color: '#212529' }}>
                                Powód odrzucenia (opcjonalnie)
                              </Form.Label>
                              <Form.Control
                                as="textarea" rows={2}
                                value={responseNote}
                                onChange={e => setResponseNote(e.target.value)}
                                style={{ borderRadius: '10px', color: '#212529' }}
                              />
                            </Form.Group>
                            <div className="d-flex gap-2 justify-content-end">
                              <button
                                className="btn btn-sm btn-light"
                                style={{ borderRadius: '10px' }}
                                onClick={() => { setRejectingId(null); setResponseNote(''); }}
                              >
                                Anuluj
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                style={{ borderRadius: '10px' }}
                                onClick={() => handleReject(r.id)}
                              >
                                Potwierdź odrzucenie
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    </div>
  );
}

export default EmployerWorkerVacationRequests;